import { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from './userContext';
import { FiUsers, FiCalendar, FiUser } from 'react-icons/fi';

const UserTasks = () => {
  const { user } = useUser();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dummy tasks data for demonstration
  const dummyTasks = [
    {
      _id: '1',
      title: 'Review Quarterly Budget Reports',
      description: 'Analyze and provide feedback on the Q1 budget performance reports for all departments.',
      status: 'Pending',
      department: { name: 'Finance' },
      dueDate: '2024-02-15T00:00:00.000Z',
      createdBy: { name: 'Sarah Johnson' },
      priority: 'High'
    },
    {
      _id: '2',
      title: 'Update Employee Onboarding Materials',
      description: 'Revise the onboarding documentation to include new HR policies and procedures.',
      status: 'In Progress',
      department: { name: 'Human Resources' },
      dueDate: '2024-02-10T00:00:00.000Z',
      createdBy: { name: 'Mike Chen' },
      priority: 'Medium'
    },
    {
      _id: '3',
      title: 'Website Security Audit',
      description: 'Conduct a comprehensive security audit of the company website and identify potential vulnerabilities.',
      status: 'Completed',
      department: { name: 'IT' },
      dueDate: '2024-01-31T00:00:00.000Z',
      createdBy: { name: 'David Kim' },
      priority: 'High'
    },
    {
      _id: '4',
      title: 'Client Presentation Preparation',
      description: 'Prepare slides and materials for the upcoming client meeting with ABC Corporation.',
      status: 'Pending',
      department: { name: 'Sales' },
      dueDate: '2024-02-20T00:00:00.000Z',
      createdBy: { name: 'Emily Rodriguez' },
      priority: 'Medium'
    },
    {
      _id: '5',
      title: 'Inventory Management System Update',
      description: 'Test and deploy the new inventory management system module for the warehouse team.',
      status: 'In Progress',
      department: { name: 'Operations' },
      dueDate: '2024-02-05T00:00:00.000Z',
      createdBy: { name: 'Robert Wilson' },
      priority: 'High'
    },
    {
      _id: '6',
      title: 'Social Media Campaign Analysis',
      description: 'Analyze the performance metrics of the recent social media marketing campaign.',
      status: 'Pending',
      department: { name: 'Marketing' },
      dueDate: '2024-02-25T00:00:00.000Z',
      createdBy: { name: 'Lisa Thompson' },
      priority: 'Low'
    },
    {
      _id: '7',
      title: 'Team Building Event Planning',
      description: 'Organize and coordinate the quarterly team building activity for the department.',
      status: 'Completed',
      department: { name: 'Human Resources' },
      dueDate: '2024-01-28T00:00:00.000Z',
      createdBy: { name: 'Jennifer Martinez' },
      priority: 'Medium'
    }
  ];

  const fetchTasks = async () => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, use dummy data when API is not available or user is not logged in
      if (!user || !user.userId) {
        setTasks(dummyTasks);
        setLoading(false);
        return;
      }

      const API_URL = `${process.env.REACT_APP_API_URL}/api`;
      const token = localStorage.getItem('authToken');
      
      const res = await axios.get(`${API_URL}/tasks/${user.userId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "ngrok-skip-browser-warning": "true" 
        }
      });
      
      setTasks(res.data.data || dummyTasks); // Fallback to dummy data if API returns empty
    } catch (err) {
      console.error('Failed to fetch tasks. Using dummy data:', err);
      // Use dummy data when API fails
      setTasks(dummyTasks);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [user?.userId]);

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      // Update local state immediately for better UX
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task._id === taskId ? { ...task, status: newStatus } : task
        )
      );

      // Simulate API call for dummy data
      if (taskId.startsWith('dummy-') || dummyTasks.some(task => task._id === taskId)) {
        console.log(`Dummy task ${taskId} status updated to: ${newStatus}`);
        return;
      }

      const API_URL = `${process.env.REACT_APP_API_URL}/api`;
      const token = localStorage.getItem('authToken');
      
      await axios.patch(
        `${API_URL}/tasks/${taskId}`,
        { status: newStatus },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "true" 
          } 
        }
      );
      
      // Refresh tasks from API to ensure consistency
      // fetchTasks();
    } catch (err) {
      console.error('Status update failed:', err);
      // Revert local state change if API call fails
      fetchTasks();
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-50 text-green-700 border-green-200';
      case 'In Progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Pending': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Your Assigned Tasks</h1>
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading your tasks...</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">Your Assigned Tasks</h1>
      <p className="text-gray-600 mb-6">Manage and update the status of your assigned tasks</p>
      
      {tasks?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-100">
          <FiCalendar className="mx-auto text-gray-400 text-4xl mb-4" />
          <p className="text-gray-500 text-lg">No tasks currently assigned to you</p>
          <p className="text-gray-400 text-sm mt-2">Tasks assigned to you will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks?.map(task => (
            <div key={task._id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg text-gray-800">{task.title}</h3>
                    {task.priority && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                        {task.priority} Priority
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 leading-relaxed">{task.description}</p>
                </div>
                <select
                  value={task.status}
                  onChange={(e) => updateTaskStatus(task._id, e.target.value)}
                  className={`border rounded-lg px-4 py-2 text-sm font-medium cursor-pointer transition-colors ${getStatusColor(task.status)}`}
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Task Metadata Section */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <FiUsers className="mr-2 text-gray-400" />
                    <span className="font-medium mr-1">Department:</span>
                    <span className="text-gray-700">{task.department?.name || 'No department assigned'}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <FiCalendar className="mr-2 text-gray-400" />
                    <span className="font-medium mr-1">Due:</span>
                    <span className="text-gray-700">{new Date(task.dueDate).toLocaleDateString()}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <FiUser className="mr-2 text-gray-400" />
                    <span className="font-medium mr-1">Assigned by:</span>
                    <span className="text-gray-700">{task.createdBy?.name || 'Admin'}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserTasks;